import { DateTime } from "luxon";
import { ChoiceModel } from "../ChoiceModel";
import { QuestionModel } from "../QuestionModel";
import {
    getSettingBoolean,
    getSettingString,
    hasSetting,
    getSettingNumber,
} from "./settingUtilities";
import { cDateFormatToLuxon } from "../../utils/time";
import { isNullOrEmpty } from "../../utils/index";
import { QuestionType } from "../enums/QuestionType";
import { isSingleChoice, getVariablesCount, autoAdvanceCarouselQuestion } from "./gridUtilities";
import { ChoiceListDisplayType } from "../enums/ChoiceListDisplayType";
import { ControlProps } from "../../components/controls/BaseControl";

export function isImageAreaEvaluator(questionType: QuestionType) {
    return questionType === QuestionType.ImageAreaEvaluator;
}

export function shouldSetSoftPromptCode(question: QuestionModel) {
    return !isImageAreaEvaluator(question.type);
}

export function shouldSetDefaultChoice(question: QuestionModel) {
    return !isImageAreaEvaluator(question.type);
}

export function isQuestionSoftPrompt(question: QuestionModel) {
    return getSettingBoolean(question, "AnswerRequired", "SoftPrompt");
}

export function isAutoAdvanceCompatible(question: QuestionModel, props: ControlProps) {
    switch (question.type) {
        case QuestionType.AutoComplete:
        case QuestionType.DropDownList:
        case QuestionType.RadioButton:
        case QuestionType.RespondentLanguage:
        case QuestionType.RespondentTimeZone:
            return true;
        case QuestionType.ImageAreaSelector:
        case QuestionType.ImageSelector:
        case QuestionType.CheckBox:
            return isSingleChoice(question);
        case QuestionType.StarRatingGrid:
            return getVariablesCount(question) === 1;
        case QuestionType.NetPromoter:
        case QuestionType.ImageChoiceGrid:
        case QuestionType.ChoiceGrid:
            return isSingleChoice(question) && getVariablesCount(question) === 1;
        case QuestionType.Carrousel:
            return autoAdvanceCarouselQuestion(question, props);
        default:
            return false;
    }
}

export function isAutoAdvanceExcluded(question: QuestionModel) {
    switch (question.type) {
        case QuestionType.Description:
            return true;
        default:
            return false;
    }
}

export function dateFormat(question: QuestionModel, includeTime = true) {
    const format = cDateFormatToLuxon(getSettingString(question, "DateFormat") || "yyyy-MM-dd");
    if (includeTime) {
        const timeFormat = cDateFormatToLuxon(getSettingString(question, "TimeFormat") || "HH:mm");
        if (getSettingBoolean(question, "Type", "Time")) {
            return timeFormat;
        }
        if (getSettingBoolean(question, "Type", "DateTime")) {
            return `${format} ${timeFormat}`;
        }
    }
    return format;
}

export function dateFromValue(question: QuestionModel, value: string, includeTime = true) {
    if (isNullOrEmpty(value)) {
        return undefined;
    }
    const dateTimeFormat = dateFormat(question, includeTime);

    let date = DateTime.fromFormat(value, dateTimeFormat);
    if (date.isValid) return date;

    // 'hh:mm a' and 'hh-mm a' formats have issues with AM/PM in different languages ==> set locale to English
    if (dateTimeFormat.indexOf("hh:mm a") > -1 || dateTimeFormat.indexOf("hh-mm a") > -1) {
        date = DateTime.fromFormat(value, dateTimeFormat, { locale: "en" });
        if (date.isValid) return date;
    }

    // Arabic date formats need Arabic normalization
    const newValue = normalizeArabicToLatin(value);
    date = DateTime.fromFormat(newValue, dateTimeFormat);
    if (date.isValid) return date;

    // This is used for piped dates.
    return DateTime.fromFormat(value, cDateFormatToLuxon("dddd, MMMM D, YYYY"));
}

// Function to normalize Arabic numerals to Latin numerals
function normalizeArabicToLatin(arabicDateTime) {
    return arabicDateTime
        .replace(/[\u0660-\u0669]/g, (c) => {
            return c.charCodeAt(0) - 0x0660;
        })
        .replace(/[\u06f0-\u06f9]/g, (c) => {
            return c.charCodeAt(0) - 0x06f0;
        });
}

export function findVariablesInHtmlContent(content: string) {
    const variables: string[] = [];
    const regex = /<(input.*?type\s*=\s*(?:["']([^"']*?)["']|(\S+))|textarea|option).*?%(\w+):?(\w*):?(\w*)%/gi;
    let matches = null;
    while ((matches = regex.exec(content))) {
        if (matches === undefined) {
            continue;
        }
        const variable = matches[4].toUpperCase();
        variables.push(variable);
    }
    return variables;
}

export function getChoiceWithPredicate(
    question: QuestionModel,
    predicate: (choice: ChoiceModel) => boolean,
): ChoiceModel {
    const { variables = [] } = question;
    const choice = variables.map((variable) => variable.choiceList.find(predicate)).find(Boolean);
    return choice;
}

export function getChoiceWithValue(question: QuestionModel, value: string): ChoiceModel {
    return getChoiceWithPredicate(question, (c) => c.value.toUpperCase() === value.toUpperCase());
}

export function getChoiceWithName(question: QuestionModel, name: string): ChoiceModel {
    return getChoiceWithPredicate(question, (c) => c.name.toUpperCase() === name.toUpperCase());
}

export function getChoiceWithIndex(question: QuestionModel, index: number): ChoiceModel {
    return getChoiceWithPredicate(question, (c) => c.index === index);
}

export function getDefaultChoice(question: QuestionModel): ChoiceModel {
    return getChoiceWithPredicate(question, (c) => getSettingBoolean(c, "Default"));
}

export function shouldAutoFormat(question: QuestionModel) {
    return !hasSetting(question, "AutoFormatting") || getSettingBoolean(question, "AutoFormatting");
}

export function decimalPlaces(value: string): number {
    /* eslint-disable-next-line prefer-template */
    const match = ("" + value).match(/(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/);
    if (!match) {
        return 0;
    }
    return Math.max(0, (match[1] ? match[1].length : 0) - (match[2] ? +match[2] : 0));
}

export function defaultSize(question: QuestionModel) {
    const numericQuestionTypes = [QuestionType.NumericAnswer, QuestionType.RunningTotal];
    if (numericQuestionTypes.includes(question.type)) {
        return "Small";
    }
    return "Medium";
}

export function size(question: QuestionModel) {
    return getSettingString(question, "Size", defaultSize(question));
}

export function getActiveQuesions(questions: QuestionModel[]) {
    return questions.filter((q) => !hasSetting(q, "Active") || getSettingBoolean(q, "Active"));
}

export function getSoftPromptCode(question: QuestionModel) {
    return getSettingString(question, "SoftPromptChoiceCode");
}

export function isSoftPromptVisible(question: QuestionModel, sequencedChoices: ChoiceModel[]) {
    return sequencedChoices.some((c) => c.value === getSoftPromptCode(question));
}

export function getChoiceListDisplayType(question: QuestionModel): ChoiceListDisplayType {
    const displayOrder = getSettingString(question, "DisplayOrder");
    const numberOfColumns = getSettingNumber(question, "NumberOfColumns");
    if (displayOrder === "Horizontal" && numberOfColumns > 1) {
        return ChoiceListDisplayType.HorizontalColumns;
    }
    if (displayOrder === "Horizontal" && (!numberOfColumns || numberOfColumns <= 1)) {
        return ChoiceListDisplayType.Horizontal;
    }
    if (displayOrder !== "Horizontal" && numberOfColumns > 1) {
        return ChoiceListDisplayType.VerticalColumns;
    }
    return ChoiceListDisplayType.Vertical;
}
